<template>
    <div id="hotspot-overlay">
        <hotspot-element
            v-for="(hotspot, index) in getHotspots"
            :key="index"
            :hotspot="hotspot"
            :index="index"
            @autoEventByTime="handleAutoEventByTime"
            @interactionEvent="handleInteractionEvent"></hotspot-element>
    </div>
</template>

<script setup>
import { computed, nextTick } from 'vue';
import { usePlayerStore } from '@/playerVue3/storage/storePlayer';
import HotspotElement from "@/playerVue3/components/hotspot/hotspot-element.vue";
import { ACTION_TYPE, HOTSPOT_TYPE } from "@/playerVue3/enums/hotspot-enum";
import { downloadPDF, getImageCertificate } from "@/playerVue3/services/download-service";

// INITIALIZE STORE & COMPOSABLES
const store = usePlayerStore();

// PROPS

// REACTIVE VARIABLES

// COMPUTED PROPERTIES
const getHotspots = computed(() => store.getHotspotsData);
const playerIsReady = computed(() => store.getPlayerIsReady);
const videoId = computed(() => store.getVideoID);
const videoData = computed(() => store.getVideoData);

// METHODS
function openCertificate(mode = 'view') {
    // 1. Armamos la ruta dinámica a tu backend
    const url = `/elearning/certificate/generate/pdf?videoID=${videoId.value}&mode=${mode}`;

    // 2. Le decimos al navegador que la abra en una pestaña nueva
    window.open(url, '_blank');
}

const isValidUrl = (string) => {
    if (string && string.length > 1 && string.slice(0, 2) === '//') {
        string = 'http:' + string;
    }
    try {
        let url = new URL(string);
        return !!(url.hostname && /^(http|https|ftp):\/\/[^ "]+$/.test(url.href));
    } catch (_) {
        return false;
    }
};

const handleHyperLink = async (hotspot) => {
    if (hotspot.hotspotAction.parameter !== '' || hotspot.hotspotAction.fieldExcelId !== null) {
        const weblinkID = store.getWeblinkID;
        let urlHyperlink = hotspot.hotspotAction.parameter;
        if (hotspot.hotspotAction.fieldExcelId !== null && weblinkID > 0) {
            const variableValue = store.getDataVariableById(hotspot.hotspotAction.fieldExcelId);
            if (variableValue.length > 0) {
                urlHyperlink = variableValue[0].description;
            }
        }

        if (urlHyperlink !== '') {
            const pathFile = store.getPathLocalOrServer;
            const extFile = urlHyperlink.substring(urlHyperlink.lastIndexOf(".")+1)
            const validExt = [ 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv' ];
            let folderFile = 'images_variables';
            if (validExt.indexOf(extFile) > -1) {
                folderFile = 'files_variables';
            }
            switch (parseInt(hotspot.hotspotAction.permitDownload)) {
                case 0:
                    if (isValidUrl(urlHyperlink)) {
                        window.open(urlHyperlink);
                    } else {
                        window.open(pathFile + folderFile + "/" + urlHyperlink);
                    }
                    break;
                case 1:
                    const isPdf = urlHyperlink.includes('.pdf');
                    const hasHTTP = urlHyperlink.includes('http');
                    if (isPdf && !hasHTTP) {
                        await downloadPDF(pathFile + folderFile + "/" + urlHyperlink);
                    } else if (isPdf && hasHTTP) {
                        await downloadPDF(urlHyperlink);
                    } else {
                        if (isValidUrl(urlHyperlink)) {
                            window.open(urlHyperlink);
                        } else {
                            window.open(pathFile + folderFile + "/" + urlHyperlink);
                        }
                    }
                    break;
            }
        }
    }
};

const handleInteractionEvent = async (hotspot) => {
    //Validate if video has to pause
    if (hotspot.videoPause) {
        store.getPlayer.pause();
    }
    store.addActiveHotspot(hotspot);
    switch (Number(hotspot.hotspotAction.actionType.actionTypeId)) {
        case ACTION_TYPE.HYPERLINK:
            handleHyperLink(hotspot);
            break;
        case ACTION_TYPE.TIME_JUMP:
            store.getPlayer.seek(hotspot.hotspotAction.parameterDecimal);
            nextTick(() => {
                store.addActiveHotspot(null);
            });
            store.getPlayer.play();
            break;
        case ACTION_TYPE.MOVE_TO_CAMPAIGN:
            location.href = '../pages/video.php?vid=' + hotspot.hotspotAction.parameter;
            break;
        case ACTION_TYPE.SHOW_CERTIFICATE:
            if (parseInt(hotspot.hotspotAction.permitDownload) == 0) {
                openCertificate('view');
            } else if (parseInt(hotspot.hotspotAction.permitDownload) > 0) {
                openCertificate('download');
            }
            break;
        case ACTION_TYPE.SOCIAL_SHARE:
            let parameter = JSON.parse(hotspot.hotspotAction.parameter);
            if (parameter) {
                const text = parameter.text_post;
                let imageShare = '';

                // 👇 Abrimos la ventana YA, en blanco, de forma síncrona
                const popup = window.open(
                    '',
                    'ShareOnLinkedIn',
                    `width=642,height=796,top=${(window.screen.height / 2) - 398},left=${(window.screen.width / 2) - 321},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`
                );

                if (popup) {
                    contentHtmlWaiting(popup);
                }

                if (parameter.attachment.type == 'certificate') {
                    const responseImage = await getImageCertificate(videoId.value);
                    if (responseImage) {
                        imageShare = responseImage.data.imageUrl;
                    }
                }
                if (parameter.attachment.type == 'custom_image') {
                    imageShare = parameter.attachment.url;
                }

                prepareDataToShare(text, imageShare, videoData.value.company.id, popup); // le pasamos la referencia
            }
            break;
        default:
            //Set the active modal via vuex
            break;
    }
};

const contentHtmlWaiting = (popup) => {
    popup.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
            <title>Loading...</title>
            <style>
                body {
                    margin: 0;
                    height: 100vh;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    background: #ffffff;
                    font-family: -apple-system, sans-serif;
                }
                .spinner {
                    width: 40px;
                    height: 40px;
                    border: 4px solid #e0e0e0;
                    border-top: 4px solid #0a66c2;
                    border-radius: 50%;
                    animation: spin 0.8s linear infinite;
                }
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
                p {
                    margin-top: 16px;
                    color: #666;
                    font-size: 14px;
                }
            </style>
        </head>
        <body>
            <div class="spinner"></div>
            <p>Preparing content to share...</p>
        </body>
        </html>
    `);
    popup.document.close(); // importante, cierra el stream de escritura
}

const handleAutoEventByTime = (hotspot) => {
    if (store.getVideoWasExecuted) {
        const actionType = hotspot.hotspotAction.actionType.actionTypeId;
        const actionShowAuto = hotspot.showAutomatically;
        const hotspotType = hotspot.hotspotType.hotspotTypeId;

        if (actionType === ACTION_TYPE.PAUSE) {
            if (playerIsReady.value === true) {
                store.getPlayer.pause();
            }
        } else if (actionType === ACTION_TYPE.TIME_JUMP && !hotspot.videoPause) {
            store.getPlayer.seek(hotspot.hotspotAction.parameterDecimal);
        } else if (hotspotType === HOTSPOT_TYPE.FORM) {
            //Set the active modal via vuex
            if (store.getVideoWasExecuted) {
                store.getPlayer.pause();
            }
            store.addActiveHotspot(hotspot);
        } else if (hotspotType === HOTSPOT_TYPE.TARGET && actionShowAuto) {
            //Set the active modal via vuex
            if (playerIsReady.value === true && hotspot.videoPause) {
                store.getPlayer.pause();
            }
            store.addActiveHotspot(hotspot);
        }
    }
};

function prepareDataToShare(textShare, imageShare, companyId, popup) {
    const BROADCAST_TYPES = {
        PUBLISHED: 'PUBLISHED',
        CANCELED: 'CANCELED',
    }
    const channel = new BroadcastChannel('share-linkedin')

    let hasFinished = false
    const startUrl = '/share-social-media/linkedin'
    const searchParams = new URLSearchParams()
    searchParams.append('text', textShare)
    searchParams.append('imageUrl', imageShare)
    searchParams.append('companyId', companyId)

    handleOpenToShare({
        url: `${startUrl}?${searchParams}`,
        popup, // la ventana que ya abrimos antes
        onClosed: () => {
            if (!hasFinished) {
                console.log('Closed manually')
            }
        }
    })

    channel.onmessage = function (event) {
        if (event.data.type === BROADCAST_TYPES.PUBLISHED) {
            hasFinished = true
            console.log('Published')
            return
        }
        if (event.data.type === BROADCAST_TYPES.CANCELED) {
            hasFinished = true
            console.log('Canceled')
        }
    }
}

function handleOpenToShare({ url, popup, onClosed }) {
    // Si ya viene una ventana abierta (caso iOS), solo navegamos
    if (popup) {
        if (popup.closed) {
            console.error('Popup blocked or closed by user')
            return
        }
        popup.location.href = url;
        popup.focus();
    } else {
        // Fallback: comportamiento original si no se pasó popup
        const popupWidth = 642;
        const popupHeight = 796;
        const left = (window.screen.width / 2) - (popupWidth / 2);
        const top = (window.screen.height / 2) - (popupHeight / 2);

        popup = window.open(
            url,
            'ShareOnLinkedIn',
            `width=${popupWidth},height=${popupHeight},top=${top},left=${left}`
        );

        if (!popup) {
            console.error('Popup blocked')
            return
        }
        popup.focus();
    }

    const timer = setInterval(() => {
        if (popup.closed) {
            clearInterval(timer)
            onClosed?.(popup)
        }
    }, 500)
}

// LYFECYCLE HOOKS
</script>
